import React, { useEffect, useState } from 'react';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { C } from '../../constants';

function Bar({ label, value, max, color }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
        <span style={{ fontSize: '0.82rem', color: '#4A5568' }}>{label}</span>
        <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#0B1D3A' }}>{value}</span>
      </div>
      <div style={{ height: '8px', background: 'rgba(26,68,128,0.08)', borderRadius: '4px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: pct + '%', background: color, borderRadius: '4px', transition: 'width 0.6s ease' }} />
      </div>
    </div>
  );
}

function StatBox({ label, value, color }) {
  return (
    <div style={{ background: 'white', borderRadius: '8px', padding: '20px 24px', border: '1px solid rgba(26,68,128,0.08)', textAlign: 'center' }}>
      <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '2.4rem', fontWeight: 700, color: color || C.navy, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: '0.75rem', color: '#8899AA', letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: '6px' }}>{label}</div>
    </div>
  );
}

export default function Analytics() {
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    const q = query(collection(db, 'bookings'), orderBy('createdAt', 'desc'));
    return onSnapshot(q, snap => setBookings(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, []);

  // By service
  const serviceCounts = {};
  bookings.forEach(b => {
    const s = b.service || 'Unknown';
    serviceCounts[s] = (serviceCounts[s] || 0) + 1;
  });
  const maxService = Math.max(...Object.values(serviceCounts), 1);

  // By month
  const monthCounts = {};
  bookings.forEach(b => {
    if (!b.date) return;
    const d = new Date(b.date);
    const key = d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
    monthCounts[key] = (monthCounts[key] || 0) + 1;
  });
  const monthEntries = Object.entries(monthCounts).slice(-6);
  const maxMonth = Math.max(...monthEntries.map(([, v]) => v), 1);

  // Status breakdown
  const statusCounts = { pending: 0, completed: 0, cancelled: 0 };
  bookings.forEach(b => { const s = b.status || 'pending'; if (statusCounts[s] !== undefined) statusCounts[s]++; });

  // This month
  const thisMonth = new Date();
  const thisMonthBookings = bookings.filter(b => {
    if (!b.date) return false;
    const d = new Date(b.date);
    return d.getMonth() === thisMonth.getMonth() && d.getFullYear() === thisMonth.getFullYear();
  });

  // Source breakdown
  const fromWebsite = bookings.filter(b => b.source !== 'manual').length;
  const fromManual = bookings.filter(b => b.source === 'manual').length;

  const serviceColors = ['#1A4480', '#C8A96E', '#059669', '#D97706', '#7C3AED', '#DC2626'];

  return (
    <div className="admin-page" style={{ padding: "32px", fontFamily: 'Jost, sans-serif' }}>
      <h1 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '2rem', fontWeight: 700, color: '#0B1D3A', marginBottom: '4px' }}>Analytics</h1>
      <p style={{ color: '#8899AA', fontSize: '0.88rem', marginBottom: '32px' }}>Overview of all appointments and booking trends.</p>

      {/* Summary stats */}
      <div className="analytics-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '14px', marginBottom: '28px' }}>
        <StatBox label="Total Bookings" value={bookings.length} color={C.navy} />
        <StatBox label="This Month" value={thisMonthBookings.length} color={C.blue} />
        <StatBox label="Completed" value={statusCounts.completed} color="#059669" />
        <StatBox label="Pending" value={statusCounts.pending} color="#D97706" />
        <StatBox label="Cancelled" value={statusCounts.cancelled} color="#DC2626" />
      </div>

      <div className="admin-analytics-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
        {/* Bookings by service */}
        <div style={{ background: 'white', borderRadius: '8px', padding: '24px', border: '1px solid rgba(26,68,128,0.08)' }}>
          <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.2rem', fontWeight: 600, color: '#0B1D3A', marginBottom: '20px' }}>Bookings by Service</h3>
          {Object.entries(serviceCounts).length === 0 ? (
            <p style={{ color: '#8899AA', fontSize: '0.88rem', textAlign: 'center', padding: '20px 0' }}>No data yet.</p>
          ) : (
            Object.entries(serviceCounts)
              .sort(([, a], [, b]) => b - a)
              .map(([service, count], i) => (
                <Bar key={service} label={service} value={count} max={maxService} color={serviceColors[i % serviceColors.length]} />
              ))
          )}
        </div>

        {/* Bookings by month */}
        <div style={{ background: 'white', borderRadius: '8px', padding: '24px', border: '1px solid rgba(26,68,128,0.08)' }}>
          <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.2rem', fontWeight: 600, color: '#0B1D3A', marginBottom: '20px' }}>Monthly Booking Trend</h3>
          {monthEntries.length === 0 ? (
            <p style={{ color: '#8899AA', fontSize: '0.88rem', textAlign: 'center', padding: '20px 0' }}>No date data yet.</p>
          ) : (
            monthEntries.map(([month, count]) => (
              <Bar key={month} label={month} value={count} max={maxMonth} color={C.blue} />
            ))
          )}
        </div>
      </div>

      {/* Status pie + source breakdown */}
      <div className="admin-analytics-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        <div style={{ background: 'white', borderRadius: '8px', padding: '24px', border: '1px solid rgba(26,68,128,0.08)' }}>
          <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.2rem', fontWeight: 600, color: '#0B1D3A', marginBottom: '20px' }}>Appointment Status</h3>
          {[['completed', '#059669', 'Completed'], ['pending', '#D97706', 'Pending'], ['cancelled', '#DC2626', 'Cancelled']].map(([key, color, label]) => (
            <Bar key={key} label={label} value={statusCounts[key]} max={bookings.length || 1} color={color} />
          ))}
        </div>

        <div style={{ background: 'white', borderRadius: '8px', padding: '24px', border: '1px solid rgba(26,68,128,0.08)' }}>
          <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.2rem', fontWeight: 600, color: '#0B1D3A', marginBottom: '20px' }}>Booking Source</h3>
          <Bar label="From Website" value={fromWebsite} max={bookings.length || 1} color={C.gold} />
          <Bar label="Manual Entry" value={fromManual} max={bookings.length || 1} color={C.blue} />
          <div style={{ marginTop: '20px', padding: '16px', background: '#F4F7FB', borderRadius: '6px' }}>
            <p style={{ fontSize: '0.82rem', color: '#4A5568', lineHeight: 1.7 }}>
              <strong>{fromWebsite}</strong> client{fromWebsite !== 1 ? 's' : ''} booked through the website form.<br />
              <strong>{fromManual}</strong> appointment{fromManual !== 1 ? 's' : ''} added manually by admin.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
